import { useState, useRef, useEffect } from "react";
import axios, { CancelTokenSource } from "axios";
import { pdfjs } from "react-pdf";
import { toast } from "sonner";
import {
  generateSessionFilePresignedUploadUrl,
  deleteSessionFile,
  getSessionFile,
  loadConversation,
} from "../service/apiService";
import { useUser } from "../context/UserContext";
import { useSession } from "../context/SessionContext";
import { useModel } from "../context/ModelContext";

// Use a specific version that's available on CDN
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// A stored file name is minted once at ingestion: "{sanitized base}-{8 hex}{ext}".
// The base uses only Bedrock-legal characters (alphanumerics, space, - ( ) _ ;
// brackets excluded because the citation parser cannot carry them), so the SAME
// string is safe as the S3 key basename, the message-meta join key, and the
// Bedrock document label — and the hex suffix gives every upload a unique,
// citation-stable identity (no dedupe or byte-clamping needed: the base is capped
// and ASCII by construction). The user's real name is kept for display only and
// sent to the backend as original_file_name. Mirrors the backend's
// session_file_matching.build_stored_file_name.
const sanitizeLabelBase = (base: string): string => {
  let safe = base.replace(/[^a-zA-Z0-9 \-()_]/g, "_");
  safe = safe.replace(/\s+/g, " ");
  safe = safe.replace(/_{2,}/g, "_").replace(/^[ _]+|[ _]+$/g, "");
  return safe;
};

const randomHexSuffix = (): string => {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

const buildStoredFileName = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  const ext = dot > 0 ? fileName.slice(dot) : "";
  const base = dot > 0 ? fileName.slice(0, dot) : fileName;
  const safeBase = (sanitizeLabelBase(base) || "document").slice(0, 180);
  const safeExt = ext.replace(/[^A-Za-z0-9.]/g, "");
  return `${safeBase}-${randomHexSuffix()}${safeExt}`;
};

// storedName -> the user's original name, for display and message meta. Module
// level so chips can resolve names without prop drilling; a stored name from a
// reloaded session (not in the map) falls back to hiding its suffix.
const originalNames = new Map<string, string>();
const STORED_SUFFIX_RE = /-[0-9a-f]{8}(?=\.[A-Za-z0-9]+$|$)/;

// Collapse an accidentally doubled trailing extension ("report.pdf.pdf" ->
// "report.pdf", "sheet.xlsx.xlsx" -> "sheet.xlsx"). Only collapses when the SAME
// extension repeats back-to-back, so legitimate names like "archive.tar.gz" are
// left untouched. Display-only: the stored name / download key is never changed.
export const dedupeTrailingExt = (name: string): string =>
  name.replace(/(\.[A-Za-z0-9]+)\1$/i, "$1");

export const getDisplayFileName = (name: string): string =>
  dedupeTrailingExt(originalNames.get(name) ?? name.replace(STORED_SUFFIX_RE, ""));

// Register a stored→original name so getDisplayFileName resolves it. Used by chat
// uploads (this session) and by brainbank source citations (fetched per index).
// Also key the extension-less stored name: the model occasionally cites a source
// without its extension, and getDisplayFileName looks up the cited string verbatim.
export const registerOriginalName = (
  storedName: string,
  originalName: string
): void => {
  if (!storedName || !originalName) return;
  originalNames.set(storedName, originalName);
  const dot = storedName.lastIndexOf(".");
  if (dot > 0) originalNames.set(storedName.slice(0, dot), originalName);
};

export const useFileUpload = () => {
  const { userId, userDetails } = useUser();
  const { selectedModel } = useModel();
  const { sessionId, createSessionId, setSessionId } = useSession();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number | "failed";
  }>({});
  const [fileIdMap, setFileIdMap] = useState<{ [key: string]: string }>({});
  const [fileStatusMap, setFileStatusMap] = useState<{ [key: string]: string }>(
    {}
  );
  const [uploading, setUploading] = useState(false);
  const cancelTokens = useRef<{ [key: string]: CancelTokenSource }>({});
  const [removingFileIndex, setRemovingFileIndex] = useState<number | null>(
    null
  );

  // Track total session files
  const [totalSessionFileCount, setTotalSessionFileCount] = useState<number>(0);

  // Store userId in a ref to always access the latest value even if component rendered before auth
  const userIdRef = useRef(userId);
  const userDetailsRef = useRef(userDetails);
  
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    userDetailsRef.current = userDetails;
  }, [userDetails]);

  // Always get real-time file count from session
  useEffect(() => {
    const getSessionFileCount = async () => {
      if (sessionId) {
        try {
          const conversation = await loadConversation(sessionId);
          let totalFilesInSession = 0;
          
          // Count ALL files in the entire conversation
          conversation.forEach((message: any) => {
            if (message.content?.meta?.files && Array.isArray(message.content.meta.files)) {
              totalFilesInSession += message.content.meta.files.length;
            }
            
            // Also check other possible file locations
            const locations = [message.files, message.content?.files, message.attachments, message.uploaded_files];
            locations.forEach((location) => {
              if (location && Array.isArray(location)) {
                totalFilesInSession += location.length;
              }
            });
          });
          
          setTotalSessionFileCount(totalFilesInSession);
          
        } catch (error) {
          console.log("New session - no existing files");
          setTotalSessionFileCount(0);
        }
      } else {
        setTotalSessionFileCount(0);
      }
    };

    getSessionFileCount();
  }, [sessionId]);

  // Updated file validation constants based on new rules
  const MAX_FILES = 5; // New maximum of 5 documents per session
  const MAX_FILE_SIZE_MB = 4.5; // 4.5 MB maximum file size
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_IMAGES = 20; // Maximum of 20 images per request
  const MAX_IMAGE_SIZE_MB = 3.75; // 3.75 MB maximum image size
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
  const MAX_IMAGE_DIMENSION = 8000; // 8000px maximum image dimension

  // Check session file limit ONLY
  const handleFilesSelected = async (files: File[]) => {
    const currentUserId = userIdRef.current;
    
    if (!currentUserId) {
      toast.error("Please wait for authentication to complete before uploading files");
      return;
    }
    
    // Get fresh count from session before validation
    let currentSessionFileCount = totalSessionFileCount;

    // If we have a session, get the most up-to-date count
    if (sessionId) {
      try {
        const conversation = await loadConversation(sessionId);
        let totalFilesInSession = 0;

        conversation.forEach((message: any) => {
          if (message.content?.meta?.files && Array.isArray(message.content.meta.files)) {
            totalFilesInSession += message.content.meta.files.length;
          }
          
          const locations = [message.files, message.content?.files, message.attachments, message.uploaded_files];
          locations.forEach((location) => {
            if (location && Array.isArray(location)) {
              totalFilesInSession += location.length;
            }
          });
        });
        
        currentSessionFileCount = totalFilesInSession;
        setTotalSessionFileCount(totalFilesInSession);
      } catch (error) {
        // If error loading, use current state
      }
    }
    
    // Session files + currently uploading + new files
    const totalAfterUpload = currentSessionFileCount + uploadedFiles.length + files.length;

    if (totalAfterUpload > MAX_FILES) {
      const remainingSlots = Math.max(0, MAX_FILES - (currentSessionFileCount + uploadedFiles.length));
      
      console.log(`❌ BLOCKED: Session would have ${totalAfterUpload} files (limit: ${MAX_FILES})`);
      
      if (remainingSlots === 0) {
        toast.error(
          `This session already has ${currentSessionFileCount + uploadedFiles.length} files. Maximum ${MAX_FILES} files allowed per session. Please upload to Brain Bank instead.`,
          { duration: 6000 }
        );
      } else {
        toast.error(
          `You can only upload ${remainingSlots} more file(s). You selected ${files.length} files. Please upload additional files to Brain Bank instead.`,
          { duration: 6000 }
        );
      }
      return;
    }

    const validFiles: File[] = [];
    for (const file of files) {
      // Check file size for all types of files
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.info(`File "${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit. Please upload a smaller file.`);
        continue;
      }

      // For images, validate dimensions and size
      else if (file.type.startsWith("image/")) {
        try {
          const isValid = await validateImage(file);
          if (!isValid) continue;
        } catch (error) {
          console.error("Error validating image:", error);
          toast.info(`Could not validate image "${file.name}". Please try again.`);
          continue;
        }
      }

      // Mint the stored name once, here, so the SAME name is used for the S3
      // key, the message metadata, and every upload-state map keyed by
      // file.name. The user's original name survives only in originalNames /
      // message meta's original_file_name.
      const storedName = buildStoredFileName(file.name);
      originalNames.set(storedName, file.name);
      validFiles.push(
        new File([file], storedName, {
          type: file.type,
          lastModified: file.lastModified,
        })
      );
    }

    if (validFiles.length === 0) return;

    let session_id = sessionId;
    if (!session_id) {
      const modelId = selectedModel?.foundation_model_id
      session_id = await createSessionId(modelId);
      setSessionId(session_id);
    }
    
    setUploadedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    validFiles.forEach((file) => startFileUpload(file, session_id));
  };

  const validateImage = async (file: File) => {
    // Check image size
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.info(`Image "${file.name}" exceeds the ${MAX_IMAGE_SIZE_MB}MB size limit. Please upload a smaller image.`);
      return false;
    }

    // Check image dimensions
    try {
      const imageDimensions = await getImageDimensions(file);
      
      if (imageDimensions.width > MAX_IMAGE_DIMENSION || imageDimensions.height > MAX_IMAGE_DIMENSION) {
        toast.info(`Image "${file.name}" dimensions (${imageDimensions.width}×${imageDimensions.height}) exceed the maximum allowed dimension of ${MAX_IMAGE_DIMENSION}px. Please upload a smaller image.`);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error getting image dimensions:", error);
      toast.info(`Could not validate dimensions of "${file.name}". Please try again.`);
      return false;
    }
  };

  const getImageDimensions = async (file: File): Promise<{width: number, height: number}> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const removeFile = async (fileIndex: number) => {
    const file = uploadedFiles[fileIndex];
    const fileName = file.name;

    setRemovingFileIndex(fileIndex);

    if (cancelTokens.current[fileName]) {
      cancelTokens.current[fileName].cancel("Upload canceled by user.");
      delete cancelTokens.current[fileName];
    }

    const fileId = fileIdMap[fileName];

    try {
      if (fileId) {
        await deleteSessionFile(fileId);
        console.log(`File with id ${fileId} deleted successfully.`);
        
        // Decrease total session file count when file is deleted from backend
        setTotalSessionFileCount(prev => {
          const newCount = Math.max(0, prev - 1);
          return newCount;
        });
      }
    } catch (error) {
      console.error(`Failed to delete file with id ${fileId}:`, error);
    }

    setUploadedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== fileIndex)
    );
    setUploadProgress((prevProgress) => {
      const newProgress = { ...prevProgress };
      delete newProgress[fileName];
      return newProgress;
    });
    setFileIdMap((prevMap) => {
      const newMap = { ...prevMap };
      delete newMap[fileName];
      return newMap;
    });
    setFileStatusMap((prevStatusMap) => {
      const newStatusMap = { ...prevStatusMap };
      delete newStatusMap[fileName];
      return newStatusMap;
    });

    setRemovingFileIndex(null);
    toast.success("File removed successfully.");
  };

  const startFileUpload = async (file: File, session_id: string) => {
    try {
      setUploading(true);
      
      const currentUserId = userIdRef.current;
      const currentUserDetails = userDetailsRef.current;
      
      const created_by = currentUserId || currentUserDetails?.id || "";
      const updated_by = currentUserId || currentUserDetails?.id || "";

      const { file_id, presigned_url } =
        await generateSessionFilePresignedUploadUrl(
          file.name,
          file.size.toString(),
          session_id,
          originalNames.get(file.name) ?? file.name
        );

      const cancelTokenSource = axios.CancelToken.source();
      cancelTokens.current[file.name] = cancelTokenSource;

      setFileIdMap((prevMap) => ({
        ...prevMap,
        [file.name]: file_id,
      }));

      const uploadHeaders = {
        "Content-Type": file.type,
        "x-amz-meta-created_by": created_by,
        "x-amz-meta-updated_by": updated_by,
        "x-amz-meta-session_id": session_id,
        "x-amz-meta-file_id": file_id,
        "x-amz-meta-file_size": file.size.toString(),
      };

      await axios.put(presigned_url, file, {
        headers: uploadHeaders,
        cancelToken: cancelTokenSource.token,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [file.name]: percentCompleted,
          }));

          if (percentCompleted === 100) {
            setFileStatusMap((prevStatusMap) => ({
              ...prevStatusMap,
              [file.name]: "processing",
            }));
          }
        },
      });

      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [file.name]: 100,
      }));

      // Start polling for the actual processing status
      pollFileStatus(file_id, file.name);

      delete cancelTokens.current[file.name];
      toast.success(`File "${getDisplayFileName(file.name)}" uploaded successfully. Please wait while it is processing.`);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Upload canceled", (error as Error).message);
      } else {
        console.error("Error uploading file:", (error as Error).message);
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [file.name]: "failed",
        }));
        setFileStatusMap((prevStatusMap) => ({
          ...prevStatusMap,
          [file.name]: "failed",
        }));
        toast.info(`Failed to upload "${getDisplayFileName(file.name)}".`);
        
        // Remove failed file from uploadedFiles
        setUploadedFiles((prevFiles) => 
          prevFiles.filter(f => f.name !== file.name)
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const pollFileStatus = (fileId: string, fileName: string) => {
    const intervalId = setInterval(async () => {
      try {
        const fileData = await getSessionFile(fileId);
        const fileStatus = fileData.status;
        console.log(`This file is ${fileStatus}: ${fileData}`);

        setFileStatusMap((prevStatusMap) => ({
          ...prevStatusMap,
          [fileName]: fileStatus,
        }));

        if (fileStatus === "failed") {
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [fileName]: "failed",
          }));
          toast.info(`Processing failed for "${getDisplayFileName(fileName)}".`);
        }

        if (fileStatus === "processed") {
          toast.success(`File "${getDisplayFileName(fileName)}" processed successfully.`);
        }

        if (fileStatus === "processed" || fileStatus === "failed") {
          clearInterval(intervalId); // Stop polling when file is processed or failed
        }
      } catch (error) {
        console.error(`Error polling file status for ${fileName}:`, error);
      }
    }, 3000); // Poll every 3 seconds
  };

  // Just clear upload state, let useEffect handle count
  const clearUploadedFiles = () => {
    // Cancel any ongoing uploads
    Object.values(cancelTokens.current).forEach(token => {
      token.cancel("Upload canceled by user.");
    });
        
    setUploadedFiles([]);
    setUploadProgress({});
    setFileIdMap({});
    setFileStatusMap({});
  };

  return {
    uploadedFiles,
    uploadProgress,
    fileStatusMap,
    fileIdMap,
    uploading,
    removingFileIndex,
    handleFilesSelected,
    removeFile,
    startFileUpload,
    clearUploadedFiles,
  };
};

export default useFileUpload;